import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Buildings, Warning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, setUser } = useAuth();
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (location.state?.error) setError(location.state.error);
  }, [location.state]);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      setUser(res.data);
      toast.success(`Welcome, ${res.data.name}`);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const targetEmail = forgotEmail.trim().toLowerCase();
    if (!targetEmail) {
      toast.error("Please enter your email");
      return;
    }
    setForgotSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: targetEmail });
      setForgotSent(true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset email");
    } finally {
      setForgotSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="login-page">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_role-flow-hub/artifacts/2gzvteft_ChatGPT%20Image%20Apr%2021%2C%202026%2C%2010_09_19%20AM.png')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-primary/30" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-md shadow-lg mb-6">
              <Buildings size={32} weight="bold" className="text-primary" />
            </div>
            <h1 className="text-4xl font-black text-white tracking-tight mb-2">CME PeopleHub</h1>
            <p className="text-white/70 text-lg">Your Workplace, Connected</p>
          </div>

          <div className="bg-white rounded-md shadow-2xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-foreground mb-2">Welcome Back</h2>
              <p className="text-muted-foreground text-sm">Sign in to access your workspace</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3" data-testid="login-error">
                <Warning size={20} className="text-destructive mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-destructive">Login Failed</p>
                  <p className="text-sm text-destructive/80">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.name@company.com"
                  data-testid="login-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  autoComplete="current-password"
                />
              </div>
              <Button type="submit" className="w-full h-11 font-semibold" disabled={submitting} data-testid="password-login-btn">
                {submitting ? "Signing in..." : "Sign In"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setForgotSent(false); setForgotEmail(""); setForgotOpen(true); }}
                  className="text-sm text-primary font-medium hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-center text-muted-foreground">
                Accounts are created by your Super Admin.
                <br />
                Contact IT support if you need access.
              </p>
            </div>
          </div>

          <p className="text-center text-white/50 text-sm mt-8">
            CME Envirosystems Pvt. Ltd. &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              Enter your work email. If we find an account, we'll send you a link to set a new password. The link is valid for 30 minutes.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
              <p className="font-semibold mb-1">Check your inbox</p>
              <p>If <strong>{(forgotEmail || email).trim().toLowerCase()}</strong> is a registered account, you'll receive an email shortly with reset instructions.</p>
              <p className="mt-2 text-xs text-emerald-800/80">Didn't receive it? Check spam, then try again in a minute or contact IT support.</p>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your.name@cmenvirosystems.com"
                  data-testid="forgot-email-input"
                  autoFocus
                  autoComplete="off"
                  name="forgot-email-address"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} data-testid="forgot-cancel-btn">Cancel</Button>
                <Button type="submit" disabled={forgotSubmitting} data-testid="forgot-submit-btn">
                  {forgotSubmitting ? "Sending..." : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
